/* Copyright (c) 2020-present CloudIO Inc */

import { camelCase } from 'lodash-es';
import { getManager } from '../../registry/Manager';
import { Platform } from '../../platform/Platform';
import {
  AliasType,
  ComponentManager,
  ComponentManagerFn,
  PropertyDefinition,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { DataStoreItem, defaultValue } from './DataStore';
import { analyzeDataStore } from './DataStoreAnalyze';
import { emptyFunction } from '@cloudio-saas/statex';

export function getNextAlias(alias: string, map?: Map<string, string>) {
  if (!map) return alias;
  let _alias = alias;
  let counter = 1;
  while (map.has(_alias)) {
    _alias = `${alias}${counter++}`;
  }
  return _alias;
}

export const displayName: PropertyDefinition = {
  label: 'Display Name',
  type: 'string',
  helpText:
    'e.g. `Employee` or `Sales Order` etc. Used to refer this store on messages displayed to the user.',
};

export const hideDirtyIndicator: PropertyDefinition = {
  label: 'Hide Dirty Indicator',
  type: 'boolean',
  helpText:
    'Check this to hide the dirty indicator displayed on the top left cornor of the field or grid cell when the data is updated by the user.',
};

export const controlledBy: PropertyDefinition = {
  label: 'Controlled By',
  type: 'ItemID',
  initialFilter: 'SmartSearch',
  validateItemId: (value, { pageId, appUid, itemId, platform }) => {
    const item = platform.getItem(appUid, pageId, value);
    if (item?.type !== 'SmartSearch') {
      return `Item id '${value}' doesn't belong to a Smart Search Component`;
    }
    return '';
  },
};

export const dsAppUidProp = (platform: Platform): PropertyDefinition => ({
  label: 'App',
  type: 'AppUidChooser',
  helpText: (item: DataStoreItem) => {
    if (item.props.dsAppUid === platform.appUid) {
      return "Warning! When you migrate this page to another app, it will connect to this app's data source. Leave this field blank otherwise.";
    }
    return "Leave blank to use the current app. The data will be fetched using the selected app's connection.";
  },
});

const DataStoreManager: ComponentManagerFn<'DataStore'> = (
  platform: Platform,
): ComponentManager<'DataStore'> => {
  let timer: NodeJS.Timeout;
  let callback = emptyFunction;
  return {
    ...baseComponentManager<'DataStore'>(platform, 'DataStore'),
    defaultValue,
    isChildAllowed: (page, item, childType) =>
      (
        [
          'TriggerBeforeQuery',
          'TriggerAfterQuery',
          'TriggerBeforeCreate',
          'TriggerBeforeSave',
          'TriggerAfterSave',
          'TriggerBeforeInsertStoreRecord',
          'TriggerAfterInsertStoreRecord',
          'TriggerBeforeDeleteRecord',
          'TriggerAfterDeleteRecord',
          'TriggerOnRecordActive',
        ] as ItemType[]
      ).includes(childType),
    onPropertyChange: (property, value, itemData, { appUid, pageId }) => {
      if (property === 'dsAppUid') {
        callback();
        return {
          [property]: value,
        };
      }
      if (property === 'dataSource' && typeof value === 'string') {
        const map = platform.getDataSources(appUid, pageId);
        const val = getNextAlias(`${camelCase(value)}Alias`, map);
        if (timer) {
          clearTimeout(timer);
        }
        timer = setTimeout(() => {
          platform.resetAliases(appUid, pageId);
        }, 1300);
        return {
          [property]: value,
          alias: val,
        };
      }
      return {
        [property]: value,
      };
    },
    isPropertyVisible: (property, item) => {
      switch (property) {
        case 'dsAppUid':
        case 'dataSource':
          return true;
        case 'alias':
          return !!item.dataSource;
        default:
          return !!item.alias;
      }
    },
    props: {
      dsAppUid: dsAppUidProp(platform),
      dataSource: {
        label: 'Data Source',
        type: 'DataSourceChooser',
        required: true,
        refreshCallback(cb) {
          callback = cb;
        },
      },
      alias: {
        label: 'Data Source Alias',
        type: 'DataSourceAlias',
        required: true,
      },
      displayName,
      insertable: {
        label: 'Insertable',
        type: 'booleanExpression',
      },
      updatable: {
        label: 'Updatable',
        type: 'booleanExpression',
      },
      deletable: {
        label: 'Deletable',
        type: 'boolean',
      },
      queryable: {
        label: 'Queryable',
        type: 'boolean',
      },
      autoQuery: {
        label: 'Auto Query',
        type: 'boolean',
      },
      maxRows: {
        label: 'Max Rows',
        type: 'number',
        min: 20,
        max: 2000,
        step: 20,
      },
      filterLocally: {
        label: 'Filter Locally',
        type: 'boolean',
      },
      singleton: {
        label: 'Singleton',
        type: 'boolean',
        helpText:
          'Singleton dataStores defined across different pages, with the same alias, will share the same state.',
      },
      cacheAll: {
        label: 'Cache All',
        type: 'boolean',
        helpText:
          'Check this to cache query result. With cache enabled, duplicate queries are not sent to the server.',
      },
      usePagination: {
        label: 'Use Pagination',
        type: 'boolean',
        helpText: 'Check this to use pagination component on this store.',
      },
      ignorePKDuplicate: {
        label: 'Ignore Primary Keys Duplicates',
        type: 'boolean',
        helpText:
          'Check this to ignore the duplicate primary key values on this store.',
      },
      maskPageOnQuery: {
        label: 'Mask Page On Query',
        type: 'boolean',
        helpText:
          'Check this to mask the page until the query request on store completed.',
      },
      maskPageOnPost: {
        label: 'Mask Page On Post',
        type: 'boolean',
        helpText:
          'Check this to mask the page until the post(save, server validate) request on store completed.',
      },
      doNotMonitorQueryRequest: {
        label: 'Do not Monitor Query Request',
        type: 'boolean',
        helpText: 'Check to disable monitoring query request.',
      },
      feedback: {
        label: 'Feedback Message',
        type: 'string',
        helpText:
          "The message to be shown after successful save. '#{count}' in the message will be replaced with the number of records posted. Specify NONE(without quotes) to skip it.",
      },
      controlledBy,
      hideDirtyIndicator,
    },
    category: 'Data',
    name: 'Data Store',
    canHaveChildren: () => true,
    getDataSourceAliasProperty: (type: AliasType) =>
      type === 'STORE' || type === 'QUERY_FILTER'
        ? { aliasProp: 'alias' }
        : undefined,
    getDisplayText: (item, expanded, { appUid, pageId }) => {
      if (!item.props.alias) return '';
      const page = platform.getPage(appUid, pageId);
      const itemIds = Object.keys(page);
      const usage = itemIds.filter((id) => {
        const itm = page[id];
        const mgr = getManager(itm.type);
        return mgr.isDataSourceAliasUsed(itm, item.props.alias);
      });
      return usage.length
        ? `${item.props.alias} (${usage.length} usage${
            usage.length > 1 ? 's' : ''
          })`
        : item.props.alias;
    },
    onCopy: (props, samePage) => ({
      ...props,
      alias: samePage ? '' : props.alias,
    }),
    generateShortId: (item) => item.alias,
    analyze: analyzeDataStore,
    icon: { iconName: 'database' },
    replaceableWith: [
      'ReadOnlyDataStore',
      'TransientDataStore',
      'LocalDataStore',
    ],
    description: `**Overview**<br/>
The Data Store is the fundamental component for managing data in your application. It acts as the client-side "brain" that connects your user interface to your backend Data Sources (database tables or services).
Unlike a static variable, a Data Store is intelligent: it handles fetching records, tracking changes (dirty state), managing permissions (CRUD), and synchronizing updates back to the server. Every data-driven component—from Grids and Forms to Charts and Dropdowns—relies on a Data Store to function.

**1. Connection & Identity**<br/>
To bring data into your page, you must define its source and give it a name.
* **Data Source:** Selects the server-side entity (e.g., EmployeesTable or SalesDatabase) that acts as the source of truth.
* **Data Source Alias:** A unique "nickname" for this specific instance of the data (e.g., "employeeList" or "salesData"). This is the handle you will use in all other components to bind data (e.g., setting a Grid's data source to "employeeList").

**2. Data Lifecycle & Performance**<br/>
You can control exactly how data is loaded and managed to optimize the user experience.
* **Auto Query:**
    * **Enabled:** The store automatically fetches data as soon as the page loads. Ideal for primary dashboards.
    * **Disabled:** The store waits until explicitly triggered (e.g., by a "Search" button). Ideal for reports that require user filters first.
* **Singleton:**
    * **Enabled:** The store's state is shared globally. If multiple pages use a Data Store with the exact same Alias, they share the same records. This prevents re-fetching data when users switch tabs.
* **Cache All:** Enables client-side caching. If the exact same query is requested again, the store returns the cached result instantly instead of hitting the server.
* **Use Pagination:** Tells the store to fetch data in chunks (pages) rather than downloading the entire dataset at once. Essential for large tables.
* **Max Rows:** Limits the number of records fetched in a single request (Default: 20).

**3. Permissions (CRUD Control)**<br/>
The Data Store acts as a security gatekeeper for the frontend. You can define boolean expressions to dynamically enable or disable capabilities based on the user's role or application state.
* **Insertable / Updatable / Deletable:** Expressions that return true or false. (Example: Set Deletable to \`$.session.role === 'Manager'\`. If a regular employee views the page, "Delete" buttons will automatically be disabled or hidden).
* **Queryable:** Controls whether the store is allowed to fetch data at all.

**4. User Feedback & UI**<br/>
Manage how the application communicates status to the user during data operations.
* **Mask Page On Query / Post:**
    * **Enabled:** A loading mask (spinner) covers the entire page while data is being fetched or saved, preventing user interaction until the operation completes.
* **Feedback Message:** A custom success message shown after a successful save (e.g., "Order Saved Successfully"). You can use #{count} to show how many records were affected.
* **Hide Dirty Indicator:** By default, changed fields show a small red marker (dirty flag). Enable this to suppress that visual cue if it distracts from your specific UI design.

**5. Triggers & Logic**<br/>
The Data Store is the best place to handle business logic because it centralizes the workflow. It supports a wide range of lifecycle triggers as children:
* **Before/After Query:** Run logic before fetching (e.g., "Set Filters") or after fetching (e.g., "Calculate Totals").
* **Before/After Save:** Validate data before sending it to the server, or trigger a redirect after a successful save.
* **On Record Active:** Fires whenever the user selects a different record (e.g., clicking a row in a grid). Use this to update detail views or fetch related child data.`,
  };
};

export default DataStoreManager;
